import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'vibrant' | 'electric';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  fullWidth?: boolean;
  href?: string;
  external?: boolean;
}
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  href,
  external,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center rounded-full font-semibold transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed tracking-wide';
  const variants = {
    primary: 'bg-warm-gold hover:bg-gold-hover text-white shadow-md shadow-warm-gold/20 hover:shadow-lg hover:shadow-warm-gold/30 focus:ring-warm-gold/50',
    secondary: 'bg-cream hover:bg-blush text-navy focus:ring-dusty-rose/40',
    outline: 'border border-dusty-rose hover:bg-dusty-rose/10 text-navy focus:ring-dusty-rose/40 bg-transparent',
    vibrant: 'bg-warm-gold hover:bg-gold-hover text-white shadow-lg shadow-warm-gold/40 hover:shadow-xl hover:shadow-warm-gold/60 focus:ring-warm-gold/50',
    electric: 'bg-dusty-rose hover:bg-mauve text-white shadow-md shadow-dusty-rose/20 hover:shadow-lg hover:shadow-dusty-rose/30 focus:ring-dusty-rose/50'
  };
  const sizes = {
    sm: 'px-5 py-1.5 text-xs',
    md: 'px-6 py-2 text-sm',
    lg: 'px-8 py-2.5 text-sm',
    xl: 'px-10 py-3 text-sm',
    xxl: 'px-12 py-3.5 text-base'
  };
  const classes = `
    ${baseStyles}
    ${variants[variant]}
    ${sizes[size]}
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `;
  const content = <>
      {children}
      {(variant === 'primary' || variant === 'vibrant' || variant === 'electric') && <ArrowRight className="ml-2 h-5 w-5" data-id="element-337" />}
    </>;
  if (href) {
    if (external) {
      return <motion.a href={href} target="_blank" rel="noopener noreferrer" className={classes} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} data-id="element-338">
          {content}
        </motion.a>;
    }
    return <motion.a href={href} className={classes} whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} onClick={e => {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }} data-id="element-339">
        {content}
      </motion.a>;
  }
  return <motion.button className={classes} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} {...props} data-id="element-340">
      {content}
    </motion.button>;
}